/**
 * Turns a `ClientRecord` — what the API returns — into the two shapes the
 * screens render: the list row and the detail record.
 *
 * The mapping lives here rather than in `clientsService` because none of it is
 * about the wire: it is dates written the way the design writes them, a name
 * joined out of two fields, and the backend's status constant written as the
 * badge a row wears. The plan hands over the record; what a screen makes of
 * it is this.
 *
 * Nothing on the detail record is a stand-in any more: the meal plans come from
 * `/api/v1/meal-plans/` and the subscription banner from the client's payment
 * requests, each fetched by the tab that shows it.
 */

import type { PreconsultationFormSummary } from "../preconsultation/services/preconsultationFormsService";
import { habitLabel } from "../preconsultation/preconsultationSteps";
import { newsSubjectOf } from "./clientNews";
import type {
  Client,
  ClientBodyDetail,
  ClientDayDetail,
  ClientDetail,
  ClientFoodDetail,
  ClientOtherDetails,
  ClientRecord,
  ClientStatus,
  ClientSubscription,
} from "./types/clients.types";

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

/** A date the backend sent as a day rather than a moment: `2026-08-17`. */
const DATE_ONLY = /^\d{4}-\d{2}-\d{2}$/;

/**
 * `null` for anything the backend left empty or sent unparseable.
 *
 * A day is read as that day *here*, not at UTC midnight: `new Date("2026-08-17")`
 * is midnight in London, which is still the 16th in New York — and a date the
 * backend sent without a time should not shift by a day depending on who is
 * looking at it.
 */
const parseDate = (value: string | null | undefined): Date | null => {
  if (!value) return null;
  const trimmed = value.trim();
  const date = DATE_ONLY.test(trimmed)
    ? new Date(`${trimmed}T00:00:00`)
    : new Date(trimmed);
  return Number.isNaN(date.getTime()) ? null : date;
};

/** "1 Jul 2026" — the table's date column. */
export const formatShortDate = (value: string | null): string => {
  const date = parseDate(value);
  if (!date) return "-";
  return `${date.getDate()} ${MONTHS[date.getMonth()].slice(
    0,
    3
  )} ${date.getFullYear()}`;
};

/** "26 March 2002" — the detail screen writes the month out in full. */
export const formatLongDate = (value: string | null): string => {
  const date = parseDate(value);
  if (!date) return "-";
  return `${date.getDate()} ${MONTHS[date.getMonth()]} ${date.getFullYear()}`;
};

/**
 * "Sep 6, 8:02 pm" - a moment rather than a day.
 *
 * The one place the app dates something to the minute: a pre-consultation that
 * came back an hour ago and one that came back last Tuesday are different
 * things to the practitioner reading the strip, and a date alone cannot tell
 * them apart on the day it arrives. Local time, like every other date here -
 * the wire sends UTC and the reader is in one place.
 */
export const formatDateTime = (value: string | null): string => {
  const date = parseDate(value);
  if (!date) return "-";

  const hours = date.getHours();
  const suffix = hours < 12 ? "am" : "pm";
  // Midnight and noon are 12, not 0 - `|| 12` is the whole of that rule.
  const hour = hours % 12 || 12;
  const minutes = `${date.getMinutes()}`.padStart(2, "0");

  return `${MONTHS[date.getMonth()].slice(0, 3)} ${date.getDate()}, ${hour}:${minutes} ${suffix}`;
};

/**
 * The one pre-consultation form a client's screen speaks for, out of every form
 * ever raised for them.
 *
 * Precedence rather than recency, because the strip is a thing to act on and
 * only one of the states is:
 *
 *   1. A form the client has sent back and nobody has reviewed. It is waiting
 *      on the practitioner, so it outranks everything - including a newer link
 *      raised after it, which is a practitioner who has forgotten that answers
 *      are already sitting there.
 *   2. A link still live and unanswered. Waiting on the client; there is
 *      nothing to do but wait, or send another.
 *   3. Nothing. A client with no forms, and a client whose every form has been
 *      accepted, returned or run out, are the same person to this screen: one
 *      to send a form to.
 *
 * Within a tier the newest wins, counted on `createdAt`. The backend already
 * lists them newest first, but an order that matters is one worth stating.
 */
export const governingForm = (
  forms: PreconsultationFormSummary[],
  now: Date = new Date()
): PreconsultationFormSummary | null => {
  const newestFirst = [...forms].sort(
    (a, b) => msOf(b.createdAt) - msOf(a.createdAt)
  );

  const submitted = newestFirst.find((form) => form.status === "SUBMITTED");
  if (submitted) return submitted;

  // `EXPIRED` is the backend's own word for a link that has run out, and
  // `expiresAt` is checked beside it: a form only turns expired when something
  // touches it, so a link that ran out overnight is still `PENDING` on the wire
  // the next morning. Reading the moment is what keeps the strip honest.
  return (
    newestFirst.find(
      (form) =>
        form.status === "PENDING" &&
        (!form.expiresAt || msOf(form.expiresAt) > now.getTime())
    ) ?? null
  );
};

/** A moment as a number, and 0 for one that will not parse - which sorts last. */
const msOf = (value: string | null): number =>
  parseDate(value)?.getTime() ?? 0;

/** Whole years since the date of birth. `null` when there is no date to count from. */
export const ageOn = (
  dateOfBirth: string | null,
  today: Date = new Date()
): number | null => {
  const born = parseDate(dateOfBirth);
  if (!born) return null;

  let age = today.getFullYear() - born.getFullYear();
  // The birthday has not come round yet this year.
  const monthDiff = today.getMonth() - born.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < born.getDate())) {
    age -= 1;
  }
  return age < 0 ? null : age;
};

/** Midnight, so two dates compare on the day rather than the moment. */
export const atMidnight = (date: Date): Date => {
  const copy = new Date(date);
  copy.setHours(0, 0, 0, 0);
  return copy;
};

/**
 * The day a backend date falls on, at midnight — what anything comparing dates
 * to today should measure against. `null` when there was no date to read.
 */
export const parseDay = (value: string | null | undefined): Date | null => {
  const date = parseDate(value);
  return date ? atMidnight(date) : null;
};

/**
 * How long the client's plan runs for, for the Activity column: `1 Month`, as
 * the backend words it. A client with nothing running gets the dash — they are
 * not on a plan, so there is no length to report.
 *
 * The column used to be a span counted from `created_at`, which meant every
 * client added in the same month read the same. `current_engagement_duration`
 * is the figure the design was always asking for.
 */
export const engagementLength = (duration: string | null): string =>
  (duration ?? "").trim() || "-";

/** The badge each of the backend's engagement states is written as. */
const STATUS_LABELS: Record<string, ClientStatus> = {
  ACTIVE: "Active",
  ENDING_SOON: "Ending soon",
  INACTIVE: "Inactive",
  NEW: "New",
};

/**
 * The badge for a client, out of the backend's `status`.
 *
 * Read rather than derived: "Ending soon" is a plan a few days from running
 * out, and the dates that decide it never leave the server. A state this app
 * has not heard of falls back to what the record itself says — archived is an
 * old client, and anyone else is presumed active rather than shown a badge
 * claiming their plan has lapsed.
 */
export const statusOf = (
  status: string | null | undefined,
  archivedAt: string | null = null
): ClientStatus =>
  STATUS_LABELS[(status ?? "").trim().toUpperCase()] ??
  (archivedAt ? "Inactive" : "Active");

/**
 * Both name fields joined, and never blank — a row with no name is unclickable.
 *
 * The two fields rather than a whole record, because the dashboard's recent
 * clients arrive as a trimmed one and are named by the same rule.
 */
export const fullNameOf = (
  record: Pick<ClientRecord, "firstName" | "lastName">
): string => `${record.firstName} ${record.lastName}`.trim() || "Unnamed client";

/**
 * Writes an enum constant the way a screen shows it: `MALE` reads as "Male",
 * `NON_BINARY` as "Non binary". Anything already written as a label survives
 * unchanged bar its casing, so a backend that switches to labels still works.
 */
export const formatEnumLabel = (value: string): string => {
  const words = (value ?? "").trim().replace(/_/g, " ").toLowerCase();
  return words ? words.charAt(0).toUpperCase() + words.slice(1) : "";
};

/**
 * Street, city, state and PIN code, skipping whichever came back empty.
 *
 * Ordered outward from the door, which is how an Indian address is written —
 * and note the state comes from the API's `place` field.
 */
export const addressOf = (record: ClientRecord): string =>
  [record.fullAddress, record.city, record.state, record.postalCode]
    .map((part) => (part ?? "").trim())
    .filter(Boolean)
    .join(", ") || "-";

/**
 * The plan and its duration on one line — "Weight loss | 2 months", the way
 * the Details tab writes it. Either half missing leaves the divider off rather
 * than stranding it against a blank.
 */
export const planLineOf = (
  subscription: Pick<ClientSubscription, "planName" | "duration">
): string =>
  [subscription.planName, subscription.duration]
    .map((part) => (part ?? "").trim())
    .filter(Boolean)
    .join(" | ") || "-";

/**
 * The names off a reference list, trimmed, with anything blank dropped.
 *
 * Kept as a list rather than joined into a line: the places that draw one show
 * what fits and count the rest, which is a number they can only have if they
 * are handed the answers apart. See `OneLineList`.
 */
const namesOf = (options: { name: string }[]): string[] =>
  options.map((option) => (option.name ?? "").trim()).filter(Boolean);

/**
 * What the client reacts to, named. Empty where nothing is on record — the
 * dash belongs to whatever draws them.
 */
export const allergiesOf = (record: ClientRecord): string[] =>
  namesOf(record.foodAllergies);

/**
 * What the client signed up to achieve, named. Several, since a client may be
 * working towards more than one, and empty where none is on record.
 */
export const goalsOf = (record: ClientRecord): string[] => namesOf(record.goals);

/* ── The optional details, written the way the cards show them ─────────── */

/**
 * A client with none of the optional half on record.
 *
 * The one place the two units' defaults are written down: the plan starts
 * every record from this and only replaces what actually came back, so a
 * record read off the wire and one built here cannot disagree about what a
 * client with no height is measured in. Note it is `cm` and `kg` — the unit
 * beside an empty box rather than an answer, and nothing goes back up for
 * either until there is a figure in front of it.
 */
export const NO_OTHER_DETAILS: ClientOtherDetails = {
  height: "",
  heightUnit: "cm",
  weight: "",
  weightUnit: "kg",
  targetWeight: "",
  targetWeightUnit: "kg",
  cuisines: [],
  dishesLoved: [],
  dishesAvoided: [],
  conditions: [],
  onMedication: null,
  medications: [],
  workStart: "",
  workEnd: "",
  noFixedWorkHours: false,
  sleepStart: "",
  sleepEnd: "",
  noFixedSleep: false,
  travel: "",
  alcohol: "",
  smoking: "",
};

/** The dash the detail cards use wherever nothing is on record. */
const DASH = "-";

/** Anything the record left empty reads as the dash. */
const orDash = (value: string): string => value.trim() || DASH;

/** `168cm` — the figure and its unit, closed up the way the design sets it. */
export const formatMeasure = (value: string, unit: string): string =>
  value.trim() ? `${value.trim()}${unit}` : DASH;

/**
 * `09:00` as `09:00 am`. Twelve-hour, because that is how a working day is
 * spoken about here and how the design writes it.
 */
const formatTimeOfDay = (value: string): string => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return "";

  const hours = Number(match[1]);
  if (!Number.isFinite(hours) || hours > 23) return "";

  const suffix = hours < 12 ? "am" : "pm";
  // Midnight and noon are both 12 on a twelve-hour clock — `0 % 12` is 0.
  const shown = hours % 12 === 0 ? 12 : hours % 12;
  return `${String(shown).padStart(2, "0")}:${match[2]} ${suffix}`;
};

/**
 * `09:00 am - 05:00 pm`, and the dash where either end is missing.
 *
 * Both or neither: half a range says nothing a practitioner can plan a meal
 * around, and "09:00 am -" reads as a rendering fault rather than an answer.
 */
export const formatTimeRange = (from: string, to: string): string => {
  const start = formatTimeOfDay(from);
  const end = formatTimeOfDay(to);
  return start && end ? `${start} - ${end}` : DASH;
};

/** A free list written as one line — the medications on the food card. */
const asLine = (values: string[]): string =>
  values.filter(Boolean).join(", ") || DASH;

/** The body card: height, weight, and what they are aiming for. */
export const bodyOf = (record: ClientRecord): ClientBodyDetail => {
  const {
    height,
    heightUnit,
    weight,
    weightUnit,
    targetWeight,
    targetWeightUnit,
  } = record.other;

  return {
    height: formatMeasure(height, heightUnit),
    weight: formatMeasure(weight, weightUnit),
    targetWeight: formatMeasure(targetWeight, targetWeightUnit),
  };
};

/**
 * The food card, and the dishes card under it.
 *
 * The medications line is only drawn from the list itself: a client who
 * answered No to the question has none, and "No" is not the name of a
 * medicine. What the question was answered is not shown at all — an empty
 * Medications field already says it.
 */
export const foodOf = (record: ClientRecord): ClientFoodDetail => {
  const { cuisines, conditions, medications, dishesLoved, dishesAvoided } =
    record.other;

  return {
    // Several now, and the card has one line for them — the same treatment the
    // conditions below get.
    cuisine: asLine(cuisines),
    // Several on record now, and the card has one line for them.
    conditions: asLine(conditions),
    medications: asLine(medications),
    dishesLoved,
    dishesAvoided,
    anyDishes: dishesLoved.length > 0 || dishesAvoided.length > 0,
  };
};

/** The day card: the two sets of hours, and the three habits beside them. */
export const dayOf = (record: ClientRecord): ClientDayDetail => {
  const {
    travel,
    alcohol,
    smoking,
    workStart,
    workEnd,
    sleepStart,
    sleepEnd,
  } = record.other;

  const workingHours = formatTimeRange(workStart, workEnd);
  const sleepTimings = formatTimeRange(sleepStart, sleepEnd);

  return {
    // Constants on the record — `OCCASIONAL` is not what a card shows.
    travel: orDash(habitLabel(travel)),
    alcohol: orDash(habitLabel(alcohol)),
    smoking: orDash(habitLabel(smoking)),
    workingHours,
    sleepTimings,
    any: !!(
      travel.trim() ||
      alcohol.trim() ||
      smoking.trim() ||
      workingHours !== DASH ||
      sleepTimings !== DASH
    ),
  };
};

/** A client as the list table renders one. */
export const toClientRow = (record: ClientRecord): Client => ({
  id: record.publicId,
  name: fullNameOf(record),
  goals: goalsOf(record),
  activity: engagementLength(record.engagementDuration),
  joinedOn: formatShortDate(record.createdAt),
  status: record.status,
  // The two the row's flags are read off. Carried on the row rather than
  // worked out in the table, so the reading of which form is waiting is made
  // once, here, and not once per screen that draws a client. See `clientNews`.
  createdAt: record.createdAt,
  submissionKey: newsSubjectOf(record).submissionKey,
  submittedAt: newsSubjectOf(record).submittedAt,
});

/** A client as the detail screen renders one. */
export const toClientDetail = (record: ClientRecord): ClientDetail => {
  const row = toClientRow(record);
  const age = ageOn(record.dateOfBirth);

  return {
    ...row,
    // The API's title is "Mr"; the heading writes it as "Mr.".
    salutation: record.title ? `${record.title.name}.` : "",
    // An enum constant on the wire — `MALE` is not what a screen shows.
    gender: formatEnumLabel(record.gender) || "-",
    dob: formatLongDate(record.dateOfBirth),
    age,
    lifestyle: record.lifestyle?.name ?? "-",
    profession: record.profession || "-",
    phone: record.phone || "-",
    whatsapp: record.whatsapp || "-",
    email: record.email || "-",
    address: addressOf(record),
    foodPreference: record.dietaryPattern?.name ?? "-",
    foodAllergies: allergiesOf(record),
    // Written out here rather than in the card, so what is drawn is a string
    // the mapping decided — money grouped the Indian way, the date in the same
    // long form the DOB above it is in.
    subscription: record.subscription && {
      plan: planLineOf(record.subscription),
      paidPrice: record.subscription.amount.toLocaleString("en-IN"),
      purchaseDate: formatLongDate(record.subscription.purchasedOn),
    },
    // The optional half, one card's worth each. Written here rather than in
    // the tab for the same reason the subscription above is: what a card draws
    // should be a string this mapping decided, so the two screens that will
    // one day show the same figure cannot format it differently.
    body: bodyOf(record),
    food: foodOf(record),
    day: dayOf(record),
    preconsultation: governingForm(record.preconsultationForms),
    preconsultationEverSent: record.preconsultationForms.length > 0,
  };
};
